import React from 'react';

import { Box, Grid, Typography } from '@mui/material';

import { Field, Form } from '@hrlogics/react-components/form';

import { CORPORATE_STRUCTURE_OPTIONS, INDUSTRIES_OPTIONS } from '@clearverify/constants';

import { DEFAULT_AVATAR } from 'src/assets';
import { Button } from 'src/components/common';
import { DefaultDisclaimerInputSection } from 'src/components/disclaimer';
import {
  AddressFields,
  AvatarImageUploadField,
  MaskedTextField,
  NumberField,
  PhoneField,
  SelectField,
  TextField,
} from 'src/components/finalForm';
import { TAX_YEAR_END_DATE_MASK } from 'src/constants/masks';
import { useNotification } from 'src/hooks/app';
import { useCurrentUserEmployer } from 'src/hooks/employer';
import { useFileUpload } from 'src/hooks/file';
import { validateWithYupSchema } from 'src/utils/yup';

import * as styles from './styles';
import {
  CompanyInformationFormEmployerAdminFields,
  CompanyInformationFormEmployerAdminValues,
} from './types';
import { getInitialValues } from './utils/getInitialValues';
import { companyInformationFormEmployerAdminValidationSchema } from './utils/validation';

export const CompanyInformationFormEmployerAdmin: React.FC = () => {
  const { createApolloContext } = useNotification();
  const { employer, updateEmployer } = useCurrentUserEmployer();
  const { uploadFile } = useFileUpload();

  const initialValues = React.useMemo(
    () => (employer ? getInitialValues(employer) : {}),
    [employer],
  );

  const handleSubmit = React.useCallback(
    async ({
      displayName,
      contactEmail,
      contactPhoneNumber,
      industry,
      corporateStructure,
      taxYearEndDate,
      numberOfEmployees,
      headquarters,
      logoFile,
      disclaimer,
    }: CompanyInformationFormEmployerAdminValues) => {
      try {
        let newLogoId: string | undefined;
        if (logoFile?.file && logoFile.id !== employer?.logo?.id) {
          const newLogo = await uploadFile(logoFile.file);
          newLogoId = newLogo.id;
        }

        await updateEmployer({
          data: {
            displayName,
            contactEmail: contactEmail?.toLowerCase(),
            contactPhoneNumber: contactPhoneNumber ?? null,
            industry: industry ?? null,
            corporateStructure: corporateStructure ?? null,
            taxYearEndDate: taxYearEndDate ?? null,
            numberOfEmployees,
            disclaimer: disclaimer || '',
            headquarters: {
              update: {
                apartment: headquarters?.apartment ?? null,
                city: headquarters?.city ?? null,
                county: headquarters?.county ?? null,
                state: headquarters?.state ?? null,
                street: headquarters?.street ?? null,
                zipCode: headquarters?.zipCode ?? null,
              },
            },
            logo: newLogoId ? { connect: { id: newLogoId } } : undefined,
          },
          context: createApolloContext({
            successMessage: 'Company Information has been successfully updated',
          }),
        });
      } catch (error) {
        console.error(error);
      }
    },
    [createApolloContext, employer?.logo?.id, updateEmployer, uploadFile],
  );

  return (
    <Form<CompanyInformationFormEmployerAdminValues>
      onSubmit={handleSubmit}
      initialValues={initialValues}
      validate={validateWithYupSchema(companyInformationFormEmployerAdminValidationSchema)}
      subscription={{ submitting: true, pristine: true }}
    >
      {({ submitting, pristine, form: { reset } }) => (
        <Grid container spacing={3}>
          <Grid container direction="column" size={{ xs: 12, md: 6 }}>
            <Grid>
              <Field
                name={CompanyInformationFormEmployerAdminFields.displayName}
                label="Company Display Name"
                required
                fullWidth
                disabled={submitting}
                component={TextField}
              />
            </Grid>
            <Grid>
              <Field
                name={CompanyInformationFormEmployerAdminFields.corporateStructure}
                label="Corporate Structure"
                fullWidth
                disabled={submitting}
                options={CORPORATE_STRUCTURE_OPTIONS}
                component={SelectField}
              />
            </Grid>
          </Grid>

          <Grid container size={{ xs: 12, md: 6 }}>
            <Grid size={8}>
              <Grid container direction="column" spacing={3}>
                <Grid>
                  <Field
                    name={CompanyInformationFormEmployerAdminFields.taxYearEndDate}
                    label="Tax Year End Date"
                    fullWidth
                    disabled={submitting}
                    component={MaskedTextField}
                    inputProps={{
                      placeholder: 'MM/DD',
                    }}
                    mask={TAX_YEAR_END_DATE_MASK}
                  />
                </Grid>
                <Grid>
                  <Field
                    name={CompanyInformationFormEmployerAdminFields.numberOfEmployees}
                    label="Number of Employees"
                    fullWidth
                    disabled={submitting}
                    component={NumberField}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid size={4} display="flex" alignItems="center" justifyContent="center">
              <Field
                name={CompanyInformationFormEmployerAdminFields.logoFile}
                component={AvatarImageUploadField}
                defaultImageUrl={employer?.logo?.downloadUrl || DEFAULT_AVATAR}
                avatarProps={{ sx: styles.avatarStyles }}
                disabled={submitting}
              />
            </Grid>
          </Grid>

          <Grid size={12}>
            <Field
              name={CompanyInformationFormEmployerAdminFields.industry}
              label="Industry"
              fullWidth
              disabled={submitting}
              options={INDUSTRIES_OPTIONS}
              component={SelectField}
            />
          </Grid>

          <Grid size={6}>
            <Field
              name={CompanyInformationFormEmployerAdminFields.contactPhoneNumber}
              label="Primary Contact Phone"
              fullWidth
              disabled={submitting}
              component={PhoneField}
            />
          </Grid>
          <Grid size={6}>
            <Field
              name={CompanyInformationFormEmployerAdminFields.contactEmail}
              label="Primary Contact Email"
              required
              fullWidth
              disabled={submitting}
              component={TextField}
            />
          </Grid>

          <Grid spacing={2} size={12}>
            <Grid size={12}>
              <Typography fontWeight={600}>Headquarters address</Typography>
            </Grid>
            <Grid mt={2}>
              <AddressFields
                prefix={CompanyInformationFormEmployerAdminFields.headquarters}
                requiredStreet
                requiredCity
                requiredState
                requiredZipCode
                disabled={submitting}
                addressLabel="Street"
              />
            </Grid>
          </Grid>

          <Grid size={12}>
            <DefaultDisclaimerInputSection
              name={CompanyInformationFormEmployerAdminFields.disclaimer}
            />
          </Grid>

          <Grid size={12}>
            <Box display="flex" gap={3} justifyContent="center">
              <Button
                disabled={pristine || submitting}
                variant="outlined"
                onClick={() => reset()}
                color="error"
              >
                Reset
              </Button>
              <Button
                disabled={pristine || submitting}
                loading={submitting}
                variant="contained"
                type="submit"
              >
                Save Changes
              </Button>
            </Box>
          </Grid>
        </Grid>
      )}
    </Form>
  );
};
